import { TamediaStyles, tamediaColors } from "@ta-interaktiv/microfrontend-template-helpers";
import {
  FC,
  ReactElement,
  useState,
  useEffect,
  SetStateAction,
  useMemo,
} from 'react'
import styled, { DefaultTheme, ThemeProvider } from 'styled-components'
import colorVariables from '../../Globals/colors.module.css'
import fontVariables from '../../Globals/fonts.module.css'
import Map from 'components/Map'
import { Filters } from 'components/filters'
import { RestosList } from 'components/restosList'
import { InfoLayer } from 'components/infoLayer'
import { device } from '../breakpoints'
import { MarkerData } from 'Types/markers'
import useFetch from 'react-fetch-hook'
import {
  ThemeProvider as MuiThemeProvider,
  createTheme,
  Theme,
} from '@mui/material/styles'
import { deepmerge } from '@mui/utils'

import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/grid.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/button.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/form.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/modal.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/popup.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/checkbox.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/divider.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/card.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/transition.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/search.css' // remove?
import '@ta-interaktiv/semantic-ui/semantic/dist/components/placeholder.css'
import 'mapbox-gl/dist/mapbox-gl.css'
import './styles.scss'
import {
  Button,
  TextField,
  CircularProgress,
  PaletteColorOptions,
} from '@mui/material'
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap'
import CloseIcon from '@mui/icons-material/Close'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import NearMeIcon from '@mui/icons-material/NearMe'
import MapIcon from '@mui/icons-material/Map'
import ListIcon from '@mui/icons-material/List'
import { maxWidth } from '@mui/system'
import locales from 'locales/locales'

declare module '@mui/material/styles' {
  interface CustomPalette {
    buttonDark: PaletteColorOptions
    buttonLight: PaletteColorOptions
  }
  interface Palette extends CustomPalette { }
  interface PaletteOptions extends CustomPalette { }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    buttonDark: true
    buttonLight: true
  }
}

const tenantColors = {
  light: {
    bernerzeitung: '#800000',
    tdg: '#2c3890',
    '24heures': '#d9010e',
    bund: '#0085ab',
    tagesanzeiger: '#0085ab',
  },
  dark: {
    bernerzeitung: '#9c3838',
    tdg: '#5a64a8',
    '24heures': '#A34746',
    bund: '#e9e9e9',
    tagesanzeiger: '#e9e9e9',
  }
}

const MainComponent: FC<MicroFrontendProps> = ({
  tenant,
  colorMode,
  properties,
}): ReactElement => {
  const [ref, setRef] = useState<HTMLDivElement | null>(null)
  const globalClasses = [
    colorVariables.globalColors,
    fontVariables.globalFonts,
    colorMode === 'dark' ? colorVariables.globalColorsDark : '',
  ]
  const [mainContainer, setMainContainer] = useState<HTMLDivElement | null>(
    null
  )
  const targetDomain = properties?.publisherDomain
    ? properties?.publisherDomain
    : tenant?.key
  const publisherDomain = targetDomain ? targetDomain : '24heures'
  const lang = tenant?.lang || 'fr'
  console.log('publisherDomain = ', publisherDomain)

  // Original markers list
  const [markers, setMarkers] = useState<MarkerData[]>([])

  // Filtered makers list
  const [filteredMarkers, setFilteredMarkers] = useState<MarkerData[]>([])

  // Active marker, displayed in the info window and on the map
  const [activeMarker, setActiveMarker] = useState<MarkerData | null>(null)

  // Map navigation switch
  const [mapIsLocked, setMapIsLocked] = useState(true)

  // Map / list switch
  const [showMap, setShowMap] = useState(false)

  // Search box
  const [currentSearch, setCurrentSearch] = useState('')

  // User location
  const [userLocation, setUserLocation] = useState<{
    lat: number
    lng: number
  } | null>()

  // Mobile or not
  const isMobile = window.innerWidth < 768

  var time = useMemo(() => new Date().getTime(), [])

  const publisherDomainFilename = publisherDomain === 'derbund' ? 'bernerzeitung' : publisherDomain

  // Data fetching
  const { isLoading, data, error } = useFetch(
    `https://wch-interactive.storage.googleapis.com/restaurants-${publisherDomainFilename}/markers.json?time=${time}`,
    {
      formatter: (response: Response) => response.json(),
    }
  )

  const handleSearchInput = (e: {
    target: { value: SetStateAction<string> }
  }) => {
    setCurrentSearch(e.target.value)
    mainContainer?.scrollIntoView()
  }

  const [locateMe, setLocateMe] = useState(false)
  const [showLocateMe, setShowLocateMe] = useState(true)
  const [locateFailed, setLocateFailed] = useState(false)
  const handleUserLocationFound = (lat: number, lng: number) => {
    setUserLocation({ lat, lng })
    setShowLocateMe(false)
    setLocateMe(false)
  }
  const handleUserLocationError = (e: GeolocationPositionError) => {
    alert(locales.unableToLoc[lang])
    console.warn(e)
    // We show button again but we disable it
    setLocateMe(false)
    setLocateFailed(true)
  }

  // Themes
  const { palette } = createTheme()
  const { augmentColor } = palette
  const createColor = (mainColor: any) =>
    augmentColor({ color: { main: mainColor } })
  const muiTheme = createTheme({
    typography: {
      fontFamily: 'var(--font-sans-serif)',
      htmlFontSize: 9,
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            fontSize: '1.5rem',
            textTransform: 'none',
            margin: '0 6px',
            whiteSpace: 'nowrap',
            borderRadius: '0',
            padding: '1rem 1rem',
          },
        },
      },
      // Custom properties for light/dark <TextField />
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: '#334455',
          },
          input: {
            color: colorMode === 'dark' ? '#E9E9E9' : '#334455',
            background: colorMode === 'dark' ? '#222222' : '#f2f3f4',
            border: 0,
            '&::before': {
              borderBottom: '1px solid rgba(0, 0, 0, 0.42)',
            },
          },
          notchedOutline: {
            border: 0,
            borderColor:
              colorMode === 'dark'
                ? 'rgba(200, 200, 200, 0.23)'
                : 'rgba(0, 0, 0, 0.23)',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: colorMode === 'dark' ? '#555555' : 'rgba(0, 0, 0, 0.6)',
          },
        },
      },
      MuiModal: {
        defaultProps: {
          container: ref,
        },
      },
    },
    palette: {
      primary: {
        main:
          publisherDomain in tenantColors.dark ? tenantColors[colorMode as 'dark' | 'light'][publisherDomain as "bernerzeitung" | "tdg" | "24heures" | "bund" | "tagesanzeiger"]
            : colorMode === 'dark'
              ? '#e9e9e9'
              : '#0a79bd',
      },
      secondary: { main: colorMode === 'dark' ? '#E9E9E9' : '#334455' },
      buttonDark:
        colorMode === 'light' ? createColor('#202346') : createColor('#6f7187'),
      buttonLight: createColor('#efece0'),
    },
  })
  
  const theme = {
    lang: lang,
    publisherDomain: publisherDomain,
    colorMode: colorMode,
    mainColor:
      publisherDomain in tenantColors.dark ? tenantColors[colorMode as 'dark' | 'light'][publisherDomain as "bernerzeitung" | "tdg" | "24heures" | "bund" | "tagesanzeiger"]
        : colorMode === 'dark'
          ? '#e9e9e9'
          : '#0a79bd',
    textColor: colorMode === 'dark' ? '#E9E9E9' : '#202346',
    plainColor: colorMode === 'dark' ? '#121212' : '#ffffff',
    borderColor: colorMode === 'dark' ? '#555555' : '#f1f1f1',
    greyColor: colorMode === 'dark' ? '#222222' : '#f2f3f4',
    itemBorderColor: colorMode === 'dark' ? '#555555' : '#f6f7f8',
  }

  useEffect(() => {
    // Fill the filtered markers list
    setFilteredMarkers(markers)
  }, [markers])

  useEffect(() => {
    // Fill the markers list once we retrieve the markers data
    if (data) {
      // console.log(data)
      const filteredData = data.filter(
        (marker: MarkerData) =>
          !Number.isNaN(Number(marker.latitude)) &&
          !Number.isNaN(Number(marker.longitude))
      )
      if (filteredData.length < data.length) {
        console.warn('NaN geolocation were removed.')
      }
      const now = new Date()
      const currentWeekday = now.getDay() // 0-6 Sun-Sat, same as Google Places
      // const hourMinutes =
      //   now.getHours().toString() + now.getMinutes().toString()
      // Google Places doc: https://developers.google.com/maps/documentation/places/web-service/details#PlaceOpeningHoursPeriodDetail
      filteredData.forEach((marker: MarkerData) => {
        let openToday = 'Sans indication ou fermé'
        if (marker.periods) {
          try {
            const schedules = JSON.parse(marker.periods)
            const todaySchedule = schedules.filter(
              (schedule: { open: { day: number; time: string }; close: {} }) =>
                schedule.open.day === currentWeekday
            )
            if (todaySchedule.length > 0) {
              // === Version "Ouvert en ce moment" ===
              // openToday = 'Fermé'
              // todaySchedule.forEach(
              //   (schedule: {
              //     open: { day: number; time: string }
              //     close: { day: number; time: string }
              //   }) => {
              //     if (
              //       schedule.open.time < hourMinutes &&
              //       (schedule.close.time > hourMinutes ||
              //         schedule.close.time === '0000')
              //     ) {
              //       openToday = 'Ouvert en ce moment'
              //     }
              //   }
              // )

              // === Version qui differencie midi et soir ===
              let openings = { lunch: false, dinner: false }
              todaySchedule.forEach(
                (schedule: {
                  open: { day: number; time: string }
                  close: { day: number; time: string }
                }) => {
                  if (
                    schedule.open.time < '1230' &&
                    schedule.close.time > '1230'
                  ) {
                    openings.lunch = true
                  }
                  if (
                    schedule.open.time < '1930' &&
                    (schedule.close.time > '2030' ||
                      schedule.close.time === '0000')
                  ) {
                    openings.dinner = true
                  }
                }
              )
              if (openings.dinner && openings.lunch) {
                openToday = 'Le midi / Le soir'
              } else if (openings.lunch) {
                openToday = 'Le midi'
              } else if (openings.dinner) {
                openToday = 'Le soir'
              }
            }
          } catch (err) {
            console.warn('Could not load schedule:', err)
          }
        }
        marker.filters.push({
          name: 'Ouvert aujourd’hui',
          value: openToday,
        })
      })
      setMarkers(filteredData)
    }
  }, [data])

  useEffect(() => {
    console.log(error)
  }, [error])

  return (
    <AppContainer
      id='mf-app-restos'
      className={globalClasses.join(' ')}
      ref={setRef}
    >
      <MuiThemeProvider theme={muiTheme}>
        <ThemeProvider
          theme={(outerTheme: DefaultTheme | undefined) =>
            outerTheme ? deepmerge(outerTheme, theme) : theme
          }
        >
          <Container ref={setMainContainer}>
            <Toolbar
              $showMap={showMap}
              style={{
                zIndex: activeMarker ? -1 : 999,
                transition: 'transform 0.3s ease-in-out, opacity 0.3s ease-in-out',
                opacity: activeMarker ? 0 : 1,
                transform: activeMarker
                  ? 'translateY(-300px)'
                  : 'translateY(0)',
              }}
            >
              <SearchField>
                <TextField
                  fullWidth
                  value={currentSearch}
                  onChange={handleSearchInput}
                  id='outlined-basic'
                  label={locales.searchPlaceholder[lang]}
                  variant='outlined'
                  onKeyDown={(e: any) => {
                    e.key === 'Enter' && e.target.blur()
                  }}
                />
                <MenuBlock>
                  <Filters
                    markers={markers}
                    handleFilter={(filteredMarkers: MarkerData[]) => {
                      setFilteredMarkers(filteredMarkers)
                    }}
                    currentSearch={currentSearch}
                  />

                  {isMobile && (
                    <>
                      <Button
                        color='buttonDark'
                        disableElevation
                        startIcon={showMap ? <ListIcon /> : <MapIcon />}
                        variant='contained'
                        onClick={() => {
                          setShowMap(!showMap)
                          // console.log(mainContainer?.getBoundingClientRect())
                          mainContainer?.scrollIntoView()
                          // window.scrollTo(0, 0)
                        }}
                      >
                        {showMap
                          ? locales.mapButtonList[theme.lang]
                          : locales.mapButtonMap[theme.lang]}
                      </Button>
                      <Button
                        color='buttonDark'
                        disableElevation
                        startIcon={
                          !locateMe ? (
                            <NearMeIcon />
                          ) : (
                            <ProgressIcon>
                              <CircularProgress
                                color='secondary'
                                size={'.65em'}
                              />
                            </ProgressIcon>
                          )
                        }
                        variant='contained'
                        onClick={() => {
                          setLocateMe(true)
                          showMap
                            ? setLocateMe(true)
                            : navigator.geolocation.getCurrentPosition(
                              (position) => {
                                handleUserLocationFound(
                                  position.coords.latitude,
                                  position.coords.longitude
                                )
                              },
                              (error) => {
                                handleUserLocationError(error)
                              }
                            )

                          window.dataLayer = window.dataLayer || []
                          window.dataLayer.push({
                            event: 'Interactions',
                            event_action: 'click',
                            event_label: `mylocationbutton`,
                          })
                        }}
                        disabled={locateFailed}
                      >
                        {!showMap
                          ? locales.mapButtonCloseby[theme.lang]
                          : locales.mapButtonLocateMe[theme.lang]}
                      </Button>
                    </>
                  )}
                </MenuBlock>
              </SearchField>
              <div>
                <ListResultCount>
                  {locales.searchResultCount[lang](filteredMarkers.length)}
                </ListResultCount>
              </div>
            </Toolbar>

            {/* The map container with the map activation button and locate-me button */}
            {(showMap || !isMobile) && (
              <MapContainer>
                <MapContent>
                  {/* The locate-me button */}
                  {!isMobile && (
                    <MapGeolocateButtonContainer>
                      <Button
                        color='buttonDark'
                        disableElevation
                        variant='contained'
                        startIcon={
                          !locateMe ? (
                            <NearMeIcon />
                          ) : (
                            <ProgressIcon>
                              <CircularProgress
                                color='secondary'
                                size={'.65em'}
                              />
                            </ProgressIcon>
                          )
                        }
                        onClick={() => {
                          setLocateMe(true)

                          window.dataLayer = window.dataLayer || []
                          window.dataLayer.push({
                            event: 'Interactions',
                            event_action: 'click',
                            event_label: `mylocationbutton`,
                          })
                        }}
                        disabled={locateFailed}
                      >
                        {locales.mapButtonLocateMe[lang]}
                      </Button>
                    </MapGeolocateButtonContainer>
                  )}

                  {/* The map activation button */}
                  <MapActivationButtonContainer
                    onClick={() => {
                      setMapIsLocked(!mapIsLocked)

                      window.dataLayer = window.dataLayer || []
                      window.dataLayer.push({
                        event: 'Interactions',
                        event_action: 'click',
                        event_label: `mapactivationbutton`,
                      })
                    }}
                  >
                    <Button
                      color='buttonDark'
                      disableElevation
                      variant='contained'
                      startIcon={
                        mapIsLocked ? <ZoomOutMapIcon /> : <CloseIcon />
                      }
                    >
                      {mapIsLocked
                        ? isMobile
                          ? locales.mapButtonEnterNavigationModeShort[lang]
                          : locales.mapButtonEnterNavigationMode[lang]
                        : isMobile
                          ? locales.mapButtonExitNavigationModeShort[lang]
                          : locales.mapButtonExitNavigationMode[lang]}
                    </Button>
                  </MapActivationButtonContainer>

                  {/* The map */}
                  <Map
                    handleMapLoaded={() => {
                      console.log('Map loaded!')
                    }}
                    handleMarkerClick={(marker: any) => {
                      setActiveMarker(marker)
                    }}
                    handleUserLocationFound={handleUserLocationFound}
                    handleUserLocationError={handleUserLocationError}
                    markers={filteredMarkers}
                    activeMarker={activeMarker}
                    locked={mapIsLocked}
                    publisherDomain={publisherDomain}
                    locateMe={locateMe}
                  />
                </MapContent>
              </MapContainer>
            )}

            {/* The list of restaurants */}
            <ListContainer>
              {isLoading ? (
                <PlaceholderContainer>
                  <div className='ui two column stackable grid'>
                    {new Array(6).fill(0).map((_, i) => (
                      <div key={`placeholder-column-${i}`} className='column'>
                        <div className='ui raised segment'>
                          <div className='ui placeholder'>
                            <div className='image header'>
                              <div className='line'></div>
                              <div className='line'></div>
                            </div>
                            <div className='paragraph'>
                              <div className='medium line'></div>
                              <div className='short line'></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </PlaceholderContainer>
              ) : (
                <div>
                  {(!showMap || !isMobile) && (
                    <RestosList
                      userLocation={userLocation}
                      restos={filteredMarkers}
                      handleClick={(marker: MarkerData) => {
                        setActiveMarker(marker)
                        if (mainContainer) {
                          const b = mainContainer?.getBoundingClientRect()
                          // The list should always be fully visible on the screen if a
                          // marker is selected.

                          // If the top of the list is higher than the top of the screen
                          // and the bottom of the list is lower than the bottom of the screen
                          // it means that the list is bigger than the screen.
                          // In this case we don't need to scroll
                          if (
                            b.top <= 0 &&
                            b.top + b.height >= window.innerHeight
                          ) {
                            // console.log('no need to scroll')
                            return
                          }

                          // If the top of the list is below the top of the screen
                          // We need to scroll to the top of the list
                          if (b.top > 0) {
                            // console.log('scrolling to top of the list')
                            mainContainer.scrollIntoView()
                          }

                          // If the bottom of the list is above the bottom of the screen
                          // We need to scroll to the bottom of the list
                          if (b.top + b.height < window.innerHeight) {
                            window.scrollTo(
                              0,
                              b.top +
                              window.scrollY +
                              b.height -
                              window.innerHeight
                            )
                          }
                        }
                      }}
                    />
                  )}
                </div>
              )}
              {error && <div>{locales.loadingError[lang]}</div>}
            </ListContainer>

            <InfoLayer
              activeMarker={activeMarker}
              handleClose={() => {
                setActiveMarker(null)
              }}
              publisher={publisherDomain}
            />
          </Container>
        </ThemeProvider>
      </MuiThemeProvider>
    </AppContainer>
  )
}

const Container = styled.div`
  display: block;
  position: relative;
  min-height: 100vh;
`

const MapContainer = styled.div`
  position: relative;
  top: 0;
  z-index: 2;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    height: 100vh;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    height: 100%;
    width: 50%;
    position: absolute;
    top: 0;
    right: 0;
  }
`

const MapContent = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
`

const ListContainer = styled.div`
  top: 0;
  z-index: 99;
  background-color: ${(props) => props.theme.plainColor};
  color: ${(props) => props.theme.textColor};
  line-height: 1.4em;
  position: relative;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    width: 100%;
    margin-top: 0;
    min-height: auto;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    width: 50%;
    margin-top: 0;
    min-height: auto;
  }
`

const PlaceholderContainer = styled.div`
  padding: 1em;
`

const AppContainer = styled(TamediaStyles)`
  font-family: var(--font-sans-serif);
  font-size: 1.8rem;
  line-height: 3rem;
  min-height: 100vh;
  margin-bottom: 90px;
`

const Toolbar = styled.div<{ $showMap: boolean }>`
  border-bottom: 1px solid ${(props) =>
    props.theme.colorMode === 'light' ? '#d4d4d4' : '#ffffff33'};
  // border-top: 1px solid
  //   ${(props) =>
    props.theme.colorMode === 'light' ? '#d4d4d4' : '#ffffff33'};
  background-color: ${(props) => props.theme.plainColor};
  color: ${(props) => props.theme.textColor};
  width: 100%;
  top: 0;
  z-index: 999;
  padding: 1em;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding-top: 1em;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    position: ${(props) => (props.$showMap ? 'absolute' : 'sticky')};
    top: 0;
    padding-top: 60px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    position: sticky;
    top: 0;
    padding-top: 1em;
  }

  input {
    width: 100%;
  }
`

const MenuBlock = styled.div`
  align-items: center;
  justify-content: center;
  flex-direction: row-reverse;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    display: flex;
    margin-bottom: 12px;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    display: block;
    margin-bottom: 0;
  }
`

const SearchField = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 700px;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    flex-direction: column-reverse;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    flex-direction: row;
  }
`

const MapGeolocateButtonContainer = styled.div`
  position: absolute;
  bottom: 80px;
  right: 2px;
  display: block;
  z-index: 10;
  cursor: pointer;
`

const ProgressIcon = styled.div`
  line-height: 0;

  svg {
    circle {
      stroke: white !important;
    }
  }
`

const MapActivationButtonContainer = styled.div`
  position: absolute;
  right: 2px;
  z-index: 10;
  cursor: pointer;

  @media ${device.mobileS}, @media ${device.mobileM}, @media ${device.mobileL} {
    top: auto;
    bottom: 50px;
    display: none;
  }

  @media ${device.tablet}, @media ${device.laptop} {
    top: auto;
    bottom: 25px;
    display: block;
  }
`

const ListResultCount = styled.div`
  text-align: left;
  font-size: 0.8em;
  opacity: 0.5;
  width: 100%;
  margin-top: 1em;
`

export default MainComponent
